import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import styled, { ThemeProvider } from "styled-components";
import { DarkTheme, lightTheme, mediaQueries } from "./Themes";
import Loading from "../subComponents/Loading";
import Seo from "./Seo";
import RelatedPages from "./RelatedPages";

const SocialIcons = lazy(() => import("../subComponents/SocialIcons"));
const PowerButton = lazy(() => import("../subComponents/PowerButton"));
const LogoComponent = lazy(() => import("../subComponents/LogoComponent"));
const ParticlesComponent = lazy(() =>
  import("../subComponents/ParticleComponent")
);

const Box = styled(motion.div)`
  background-color: ${(props) => props.theme.body};
  width: 100vw;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  box-sizing: border-box;
  padding: 6rem 0 6rem;
`;

// In-flow version of BigTitle: sits above the article instead of fixed
// behind it, so the blurred article box never covers it.
const Title = styled.div`
  position: relative;
  z-index: 1;
  width: 50vw;
  margin: 0 auto 1.5rem;
  color: ${(props) => `rgba(${props.theme.textRgba}, 0.2)`};
  font-size: calc(3rem + 5vw);
  line-height: 1;

  ${mediaQueries(40)`
    width: 60vw;
  `};

  ${mediaQueries(30)`
    width: auto;
    margin: 0 1rem 1rem 4.5rem;
  `};
`;

const Main = styled(motion.article)`
  border: 2px solid ${(props) => props.theme.text};
  color: ${(props) => props.theme.text};
  padding: 2rem;
  width: 50vw;
  z-index: 3;
  line-height: 1.5;
  display: block;
  font-size: calc(0.5rem + 1vw);
  backdrop-filter: blur(4px);
  background: rgb(0 0 0 / 0%);
  position: relative;
  margin: 0 auto;
  font-family: "Ubuntu Mono", monospace;
  font-style: italic;

  h1,
  h2 {
    display: block;
    font-family: "Karla", sans-serif;
    font-style: normal;
    font-weight: 600;
    margin-bottom: 0.8rem;
  }

  h2 {
    margin-top: 1.6rem;
    font-size: 1.05em;
  }

  p,
  ul {
    margin-bottom: 0.9rem;
  }

  ul {
    margin-left: 1.2rem;
  }

  a {
    color: inherit;
  }

  ${mediaQueries(40)`
    width: 60vw;
    font-size: calc(0.7rem + 0.8vw);
  `};

  ${mediaQueries(30)`
    width: auto;
    margin: 0 1rem 0 4.5rem;
    padding: 1.4rem;
    font-size: 0.95rem;
  `};
`;

const SrOnly = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const ArticlePage = ({
  seo,
  schema,
  theme = "dark",
  bigTitle,
  heading,
  related,
  noindex = false,
  children,
}) => {
  const selectedTheme = theme === "light" ? lightTheme : DarkTheme;

  return (
    <ThemeProvider theme={selectedTheme}>
      <Seo
        title={seo.title}
        description={seo.description}
        path={seo.path}
        schema={schema}
        noindex={noindex}
      />
      <Suspense fallback={<Loading />}>
        <Box
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.5 } }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
        >
          <LogoComponent theme={theme} />
          <PowerButton />
          <SocialIcons theme={theme} />
          <ParticlesComponent theme={theme} />
          <SrOnly>{heading}</SrOnly>
          <Title aria-hidden="true">{bigTitle}</Title>
          <Main
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1, delay: 0.3 } }}
          >
            {children}
            <RelatedPages links={related} />
          </Main>
        </Box>
      </Suspense>
    </ThemeProvider>
  );
};

export default ArticlePage;
